import PulseCover from '@/assets/images/pulse_cover.png';
import { useAdminContext } from '@/contexts/AdminContext';
import { TimeFilter } from '@/contexts/AdminContext/pulseProvider';
import { useAuth } from '@/contexts/AuthContext';
import Skeleton from '@common/components/Skeleton';
import { Button, Icon } from '@common/index';
import cls from 'classnames';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatModal } from './Chat';
import styles from './index.module.scss';



// Helper function to format date
const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('MMM D, YYYY');
};

const PulseList = ({onViewPulse}: { 
    onViewPulse: (pulseId: string) => void;
}) => {
    const { pulse, chat: {setCurrentConversationId} } = useAdminContext();
    const {
        userBilling: { userPlan }, openPricingModal, profile 
    } = useAuth();
    const pay_result = useRef(new URLSearchParams(window.location.search).get('pay_result'));

    const timeFilters: TimeFilter[] = ['All', 'Today', 'This Week', 'This Month'];

    const handleFilterChange = (filter: TimeFilter) => {
        pulse.setActiveFilter(filter);
    };
    const handleViewPulse = (pulseId: string) => {
        onViewPulse(pulseId);
    };

    const getEmptyText = () => {
        const isExpired = pay_result.current !== 'success' && userPlan?.endAt && new Date(userPlan.endAt) < new Date()
        const noDataSource = !profile?.shopInfos?.find(item => item.physicFlag === 1)
        
        if(isExpired) {
            return (
                <div className={styles.emptyContent}>
                    <div className={styles.emptyIcon}>
                        <Icon name="lock" size={48} />
                    </div>
                    <h2 className={styles.emptyTitle}>Unlock Everyday's Pulse</h2>
                    <Button 
                        onClick={openPricingModal}
                        color="white"
                        style={{width: 160, height: 40}}
                    >
                        Upgrade
                    </Button>
                </div>
            )
        }

        if(noDataSource) {
            return (
                <div className={styles.emptyContent}>
                    <h2 className={styles.emptyTitle}>Unable to generate new Pulse</h2>
                    <p className={styles.emptyDescription}>
                        No data sources are currently available. Please connect at least one data source to resume daily pulse.
                    </p>
                </div>
            )
        }
        
        return (
            <div className={styles.emptyContent}>
                <h2 className={styles.emptyTitle}>Generating Your Pulse...</h2>
                <p className={styles.emptyDescription}>
                    This takes a few minutes. We’ll email you when it’s ready.
                </p>
            </div>
        ) 
    }

    return (
        <div className={styles.pulseList}>
            <div className={styles.header}>
                <h1 className={styles.title}>
                    Pulse
                    <Icon name="info" size={20} color="#1A162F" tips="Your daily Pulse arrives around 7:00 AM in your local time" />
                </h1>
                <div className={styles.filterTabs}>
                    {timeFilters.map((filter) => (
                        <button
                            key={filter}
                            className={`${styles.filterTab} ${pulse.activeFilter === filter ? styles.active : ''}`}
                            onClick={() => handleFilterChange(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>
            
            {(pulse.pulseData?.length === 0) && !pulse.loading ? (
                <div className={styles.pulseGrid}>
                    <div
                        className={styles.pulseCard}
                    >
                        <div className={styles.cardImage}>
                            <img src={PulseCover} />
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.cardHeader}>
                                <h3 className={styles.cardTitle}>Bustly daily pulse</h3>
                            </div>
                            <p className={styles.cardDescription}>Get a daily pulse of your business activity</p>
                            <div className={styles.cardFooter}>
                                <div className={styles.cardMeta}>
                                    <span className={styles.cardDate}>{formatDate(new Date().toDateString())}</span>
                                </div>
                                <button 
                                    className={styles.viewButton}
                                >
                                    View Pulse
                                </button>
                            </div>
                        </div>
                        <div className={cls(styles.emptyOverlay)}>
                            {getEmptyText()}
                        </div>
                    </div>
                </div>
            ) : null}
            
            <div className={styles.pulseGrid}>
                {!pulse.loading && pulse.pulseData?.map((pulseItem) => (
                    <div
                        key={pulseItem.id}
                        className={styles.pulseCard} 
                        onClick={() => handleViewPulse(pulseItem.id)}
                    >
                        <div className={styles.cardImage}>
                            <img src={pulseItem.img_url || PulseCover} alt={pulseItem.title} />
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.cardHeader}>
                                {/* <div className={styles.cardIcon}>
                                    <Icon name="artifact" size={26} />
                                </div> */}
                                <h3 className={styles.cardTitle}>{pulseItem.title}</h3>
                            </div>
                            <p className={styles.cardDescription}>{pulseItem.brief}</p>
                            <div className={styles.cardFooter}>
                                <div className={styles.cardMeta}>
                                    <span className={styles.cardDate}>{formatDate(pulseItem.created_at)}</span>
                                </div>
                                <button 
                                    className={styles.viewButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewPulse(pulseItem.id);
                                    }}
                                >
                                    View Pulse
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {pulse.loading ? (
                    <div className={styles.pulseGrid} style={{ display: 'contents' }}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} className={cls(styles.pulseCard, styles.skeleton)}>
                                <div className={styles.cardImage}>
                                    <Skeleton width="100%" height="100%" />
                                </div>
                                <div className={styles.cardContent}>
                                    <div className={styles.cardHeader}>
                                        <Skeleton width="60%" height="24px" />
                                    </div>
                                    <Skeleton width="100%" height="16px" marginBottom="8px" />
                                    <Skeleton width="80%" height="16px" marginBottom="16px" />
                                    <div className={styles.cardFooter}>
                                        <div className={styles.cardMeta}>
                                            <Skeleton width="80px" height="14px" />
                                        </div>
                                        <Skeleton width="100px" height="32px" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export const Pulse = () => {
    const navigate = useNavigate();
    const [isChatOpen, setIsChatOpen] = useState(false);

    return (
        <>
            <PulseList
                onViewPulse={(id) => navigate(`/pulse/${id}`)}
            />
            <div className={styles.chatButtonWrapper}>
                <button className={styles.chatButton} onClick={() => setIsChatOpen(!isChatOpen)}>
                    <Icon name="chatButton" size={22}></Icon>
                    Curate Pulse
                    <Icon name="expandUp" size={18} />
                </button>
            </div>
            <ChatModal
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
            />
        </>
    )
}